import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { getSiteSettings, updateSiteSettings } from '../../../services/settingsService';

const MOVING_TYPES = [
  { key: 'normal', label: '일반이사' },
  { key: 'semi_pack', label: '반포장이사' },
  { key: 'pack', label: '포장이사' },
];

const MOVING_CONSULT = MOVING_TYPES;

const PRICING_COLUMNS = [
  { key: 'normal', partnerType: '이사', service: '일반이사', payType: '평당', source: 'moving' },
  { key: 'semi_pack', partnerType: '이사', service: '반포장이사', payType: '평당', source: 'moving' },
  { key: 'pack', partnerType: '이사', service: '포장이사', payType: '평당', source: 'moving' },
  { key: 'cleaning', partnerType: '청소', service: '입주청소', payType: '평당', source: 'cleaning' },
  { key: 'interior', partnerType: '인테리어', service: '인테리어', payType: '고정', source: 'interior' },
  { key: 'internet_only', partnerType: '인터넷', service: '인터넷 단독', payType: '고정', source: 'internet_only' },
  { key: 'internet_tv', partnerType: '인터넷', service: '인터넷·TV', payType: '고정', source: 'internet_tv' },
];

const defaultTier = { unit: 4000, complete: 80000, max: 120000, consult: 5000 };

const INDUSTRY_DEFAULTS = [
  { key: 'moving', label: '이사', consult: 5000, complete: 80000, completeRate: 10, referralRate: 5, referralMonths: 12, partnerRequest: 20000, note: '' },
  { key: 'cleaning', label: '입주청소', consult: 3000, complete: 50000, completeRate: 10, referralRate: 5, referralMonths: 12, partnerRequest: 15000, note: '' },
  { key: 'internet_tv', label: '인터넷/TV', consult: 2000, complete: 30000, completeRate: 10, referralRate: 5, referralMonths: 12, partnerRequest: 10000, note: '' },
  { key: 'interior', label: '인테리어', consult: 5000, complete: 100000, completeRate: 10, referralRate: 5, referralMonths: 12, partnerRequest: 30000, note: '' },
];

const DbPricing = () => {
  const [referral, setReferral] = useState({ rate: 5, durationMonths: 12, tier: 5 });

  const [mileage, setMileage] = useState({
    tier1: { threshold: 1000000, rate: 1 },
    tier2: { threshold: 5000000, rate: 3 },
  });

  const [movingTiers, setMovingTiers] = useState(
    MOVING_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: { ...defaultTier } }), {}),
  );
  const [movingConsult, setMovingConsult] = useState(
    MOVING_CONSULT.reduce((acc, t) => ({ ...acc, [t.key]: defaultTier.consult }), {}),
  );

  const [cleaning, setCleaning] = useState({ unit: 1500, complete: 50000, max: 80000, consult: 3000 });
  const [internetTv, setInternetTv] = useState({ unit: 1000, complete: 30000, max: 50000, consult: 2000 });
  const [internetOnly, setInternetOnly] = useState({ unit: 1000, complete: 25000, max: 40000, consult: 2000 });
  const [interior, setInterior] = useState({ unit: 5000, perPyeong: 4000, consult: 5000, max: 300000 });

  const [industries, setIndustries] = useState(INDUSTRY_DEFAULTS);

  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotDate, setSnapshotDate] = useState('');
  const [snapshots, setSnapshots] = useState([]);

  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    getSiteSettings().then((s) => {
      if (s.referralPolicy) setReferral((prev) => ({ ...prev, ...s.referralPolicy }));
      if (s.partnerMileage) setMileage((prev) => ({ ...prev, ...s.partnerMileage }));
      if (s.movingTiers) setMovingTiers((prev) => ({ ...prev, ...s.movingTiers }));
      if (s.movingConsult) setMovingConsult((prev) => ({ ...prev, ...s.movingConsult }));
      if (s.cleaningPrice) setCleaning((prev) => ({ ...prev, ...s.cleaningPrice }));
      if (s.internetPrice) setInternetTv((prev) => ({ ...prev, ...s.internetPrice }));
      if (s.internetPriceOnly) setInternetOnly((prev) => ({ ...prev, ...s.internetPriceOnly }));
      if (s.interiorPrice) setInterior((prev) => ({ ...prev, ...s.interiorPrice }));
      if (Array.isArray(s.industryDefaults)) setIndustries(s.industryDefaults);
      if (Array.isArray(s.pricingSnapshots)) setSnapshots(s.pricingSnapshots);
    }).catch(() => {});
  }, []);

  const updateTier = (key, field, value) => {
    setMovingTiers({ ...movingTiers, [key]: { ...movingTiers[key], [field]: Number(value) || 0 } });
  };

  const updateConsult = (key, value) => setMovingConsult({ ...movingConsult, [key]: Number(value) || 0 });

  // 매트릭스 셀 read/write — 다양한 source(moving/cleaning/internet_only/internet_tv/interior) 통합
  const getCell = (col, field) => {
    if (col.source === 'moving') {
      if (field === 'consult') return movingConsult[col.key] ?? 0;
      return movingTiers[col.key]?.[field] ?? 0;
    }
    if (col.source === 'cleaning') return cleaning[field] ?? 0;
    if (col.source === 'internet_tv') return internetTv[field] ?? 0;
    if (col.source === 'internet_only') return internetOnly[field] ?? 0;
    if (col.source === 'interior') {
      if (field === 'complete') return interior.perPyeong ?? 0;
      return interior[field] ?? 0;
    }
    return 0;
  };

  const setCell = (col, field, value) => {
    const v = Number(value) || 0;
    if (col.source === 'moving') {
      if (field === 'consult') {
        setMovingConsult({ ...movingConsult, [col.key]: v });
      } else {
        setMovingTiers({ ...movingTiers, [col.key]: { ...(movingTiers[col.key] || defaultTier), [field]: v } });
      }
      return;
    }
    if (col.source === 'cleaning') return setCleaning({ ...cleaning, [field]: v });
    if (col.source === 'internet_tv') return setInternetTv({ ...internetTv, [field]: v });
    if (col.source === 'internet_only') return setInternetOnly({ ...internetOnly, [field]: v });
    if (col.source === 'interior') {
      if (field === 'complete') return setInterior({ ...interior, perPyeong: v });
      return setInterior({ ...interior, [field]: v });
    }
  };

  const movingPackPreview = useMemo(() => {
    const tier = movingTiers.pack || defaultTier;
    return [20, 30].map((area) => ({
      area,
      price: Math.min(area * tier.unit, tier.max || Infinity),
    }));
  }, [movingTiers]);

  const updateIndustry = (idx, field, value) => {
    const next = [...industries];
    const val = typeof industries[idx][field] === 'number' ? Number(value) || 0 : value;
    next[idx] = { ...next[idx], [field]: val };
    setIndustries(next);
  };

  const saveAll = async () => {
    try {
      await updateSiteSettings({
        referralPolicy: referral,
        partnerMileage: mileage,
        movingTiers,
        movingConsult,
        cleaningPrice: cleaning,
        internetPrice: internetTv,
        internetPriceOnly: internetOnly,
        interiorPrice: interior,
        industryDefaults: industries,
      });
      alert('전체 가격 정책이 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const saveMovingTab = async () => {
    try {
      await updateSiteSettings({ movingTiers, movingConsult });
      alert('이사 탭이 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const saveCleaningTab = async () => {
    try {
      await updateSiteSettings({ cleaningPrice: cleaning });
      alert('입주청소 탭이 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const saveInternetTab = async () => {
    try {
      await updateSiteSettings({ internetPrice: internetTv, internetPriceOnly: internetOnly });
      alert('인터넷 탭이 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const createSnapshot = async () => {
    if (!snapshotName.trim() || !snapshotDate) {
      alert('버전명과 적용 시작일을 입력해 주세요.');
      return;
    }
    const snap = {
      id: `v_${Date.now()}`,
      name: snapshotName.trim(),
      effective_date: snapshotDate,
      data: {
        referralPolicy: referral,
        movingTiers,
        movingConsult,
        cleaningPrice: cleaning,
        internetPrice: internetTv,
        internetPriceOnly: internetOnly,
        interiorPrice: interior,
        industryDefaults: industries,
      },
      createdAt: new Date().toISOString(),
    };
    const next = [snap, ...snapshots];
    try {
      await updateSiteSettings({ pricingSnapshots: next });
      setSnapshots(next);
      setSnapshotName('');
      setSnapshotDate('');
      alert(`버전 "${snap.name}" 생성됨`);
    } catch (err) {
      alert('버전 저장 실패: ' + err.message);
    }
  };

  return (
    <Container>
      <GuideAccordion>
        <GuideHeader onClick={() => setGuideOpen((v) => !v)}>
          <span>가격·수익 구조 안내</span>
          <GuideToggle>{guideOpen ? '▲' : '▼'}</GuideToggle>
        </GuideHeader>
        {guideOpen && (
          <GuideBody>
            <GuideIntro>
              <strong>열람가</strong>는 전직 DB 구매 단가, <strong>완료가</strong>는 정산(전환수수료) 기준입니다.
              서비스별 모델: 인터넷·TV는 건당(평당 아님), 이사는 평수×이사평비 2차원, 청소는 평수×, 기타는 건당으로 저장합니다.
            </GuideIntro>
            <GuideTable>
              <thead>
                <tr><th>서비스</th><th>미수·정산(완료가) 산정</th></tr>
              </thead>
              <tbody>
                <tr><td>인터넷·TV</td><td>건당(Flat). 평당 금액 없음 — 열람가/완료가 테이블에 건당 금액</td></tr>
                <tr><td>이사</td><td>평수×구간 + 이사형태(일반/포장/반포장) 2차원 매트릭스</td></tr>
                <tr><td>청소</td><td>평수×단가 + 평수(구간별)</td></tr>
              </tbody>
            </GuideTable>
            <GuideList>
              <li><strong>수익셰어(전체완료):</strong> 공인중개사 수익 = 전환수수료(제휴 미수 합계) × 설정 비율(글로벌 기본 30). 예: 50,000원 × 30% = 15,000원.</li>
              <li><strong>추천수수료:</strong> 피추천인의 상담 전환 수수료 금액의 일정 %, 직접 추천 1단계만(A→B→C일 때 C 발생분은 B에서만). 가입 후 1년 이내 유효.</li>
              <li><strong>업체 과금:</strong> 열람가(DB 구매) · 완료가(전체완료 후 청구)</li>
            </GuideList>
          </GuideBody>
        )}
      </GuideAccordion>
      <Header>
        <Title>DB 가격 · 수수료</Title>
        <HeaderActions>
          <TextBtn onClick={() => window.location.reload()}>새로고침</TextBtn>
        </HeaderActions>
      </Header>
      <SubDesc>열람가·평당·상한과 정책 업종별 수익을 한곳에서 설정합니다.</SubDesc>

      <TwoColRow>
        <Card>
          <CardHead>
            <CardTitle>추천수익</CardTitle>
          </CardHead>
          <SubDesc>추천인 없는 회원은 미공제 · 적용: 5% / 12개월</SubDesc>
          <InlineGrid>
            <Field><Label>요율 (%)</Label>
              <NumInput type="number" value={referral.rate} onChange={(e) => setReferral({ ...referral, rate: Number(e.target.value) || 0 })} />
            </Field>
            <Field><Label>유효 기간 (개월)</Label>
              <NumInput type="number" value={referral.durationMonths} onChange={(e) => setReferral({ ...referral, durationMonths: Number(e.target.value) || 0 })} />
            </Field>
          </InlineGrid>
        </Card>

        <Card>
          <CardHead>
            <CardTitle>마일리지 (제휴업체)</CardTitle>
          </CardHead>
          <SubDesc>결제 금액 구간별 적립 요율</SubDesc>
          <InlineGrid>
            <Field>
              <Label>1단계 — 기준금액 이상 (원)</Label>
              <NumInput type="number" value={mileage.tier1.threshold} onChange={(e) => setMileage({ ...mileage, tier1: { ...mileage.tier1, threshold: Number(e.target.value) || 0 } })} />
              <SubLabel>적립 (%)</SubLabel>
              <NumInput type="number" value={mileage.tier1.rate} onChange={(e) => setMileage({ ...mileage, tier1: { ...mileage.tier1, rate: Number(e.target.value) || 0 } })} />
            </Field>
            <Field>
              <Label>2단계 — 기준금액 이상 (원)</Label>
              <NumInput type="number" value={mileage.tier2.threshold} onChange={(e) => setMileage({ ...mileage, tier2: { ...mileage.tier2, threshold: Number(e.target.value) || 0 } })} />
              <SubLabel>적립 (%)</SubLabel>
              <NumInput type="number" value={mileage.tier2.rate} onChange={(e) => setMileage({ ...mileage, tier2: { ...mileage.tier2, rate: Number(e.target.value) || 0 } })} />
            </Field>
          </InlineGrid>
          <MileagePreview>
            {Number(mileage.tier1.threshold).toLocaleString()}원↑ {mileage.tier1.rate}% · {Number(mileage.tier2.threshold).toLocaleString()}원↑ {mileage.tier2.rate}%
          </MileagePreview>
        </Card>
      </TwoColRow>

      <Card>
        <CardHead>
          <CardTitle>업종별 가격·수수료</CardTitle>
        </CardHead>
        <SubDesc>
          파트너 유형별로 열람가·완료가·최대 상한을 한 표에서 관리합니다.
          평당 = 단가 × 평수, 고정 = 건당 단일 금액.
        </SubDesc>
        <MatrixWrap>
          <PriceMatrix>
            <thead>
              <tr>
                <PMRowHeader>파트너유형</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMTh key={c.key}>{c.partnerType}</PMTh>
                ))}
              </tr>
              <tr>
                <PMRowHeader>서비스유형</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMTh key={c.key}>{c.service}</PMTh>
                ))}
              </tr>
              <tr>
                <PMRowHeader>지불유형</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMPayType key={c.key} $type={c.payType}>{c.payType}</PMPayType>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <PMRowHeader>열람가</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMTd key={c.key}>
                    <NumInput type="number" value={getCell(c, 'unit')} onChange={(e) => setCell(c, 'unit', e.target.value)} />
                  </PMTd>
                ))}
              </tr>
              <tr>
                <PMRowHeader>완료가</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMTd key={c.key}>
                    <NumInput type="number" value={getCell(c, 'complete')} onChange={(e) => setCell(c, 'complete', e.target.value)} />
                  </PMTd>
                ))}
              </tr>
              <tr>
                <PMRowHeader>최대 상한</PMRowHeader>
                {PRICING_COLUMNS.map((c) => (
                  <PMTd key={c.key}>
                    <NumInput type="number" value={getCell(c, 'max')} onChange={(e) => setCell(c, 'max', e.target.value)} />
                  </PMTd>
                ))}
              </tr>
            </tbody>
          </PriceMatrix>
        </MatrixWrap>
        <PreviewBox>
          <PreviewLabel>완료가격 예시 (포장이사 기준 실시간 미리보기)</PreviewLabel>
          <PreviewLine>
            {movingPackPreview.map((p) => (
              <PreviewItem key={p.area}>{p.area}평 = {p.price.toLocaleString()}원</PreviewItem>
            ))}
          </PreviewLine>
        </PreviewBox>
      </Card>

      <Card>
        <CardHead>
          <CardTitle>업종별 수익 (기본값)</CardTitle>
        </CardHead>
        <SubDesc>계약완료 수수료의 수익 전환 비율과 수수료 상한(CAP)을 업종별로 설정합니다.</SubDesc>
        <IndTable>
          <thead>
            <tr>
              <PTh>업종</PTh>
              <PTh>계약완료 수수료 수익 전환 비율 (%)</PTh>
              <PTh>수수료 상한(CAP) (원)</PTh>
              <PTh>비고</PTh>
            </tr>
          </thead>
          <tbody>
            {industries.map((row, i) => (
              <tr key={row.key}>
                <PTd>{row.label}</PTd>
                <PTd><NumInput type="number" value={row.completeRate} onChange={(e) => updateIndustry(i, 'completeRate', e.target.value)} /></PTd>
                <PTd><NumInput type="number" value={row.partnerRequest} onChange={(e) => updateIndustry(i, 'partnerRequest', e.target.value)} /></PTd>
                <PTd><TextInput value={row.note} onChange={(e) => updateIndustry(i, 'note', e.target.value)} placeholder="비고" /></PTd>
              </tr>
            ))}
          </tbody>
        </IndTable>
      </Card>

      <Card>
        <CardHead>
          <CardTitle>요금표 버전 (스냅샷)</CardTitle>
        </CardHead>
        <SubDesc>설정 저장 후 확정하면 이력이 남습니다. DB 생성 시 적용 요금표 추적에 사용합니다.</SubDesc>
        <BulkRow>
          <Field><Label>버전 명칭 (선택)</Label><TextInput value={snapshotName} onChange={(e) => setSnapshotName(e.target.value)} placeholder="예: 2026-03 요금표" /></Field>
          <Field><Label>적용 시작일 *</Label><TextInput type="date" value={snapshotDate} onChange={(e) => setSnapshotDate(e.target.value)} /></Field>
          <SmallBtn onClick={createSnapshot}>버전 확정</SmallBtn>
        </BulkRow>
        <HistoryTitle>최근 이력</HistoryTitle>
        {snapshots.length === 0 ? (
          <Empty>이력이 없습니다.</Empty>
        ) : (
          <HistoryList>
            {snapshots.slice(0, 5).map((s) => (
              <HistoryItem key={s.id}>
                <span>{s.name}</span>
                <span style={{ color: '#9CA3AF' }}>{s.effective_date} 적용</span>
              </HistoryItem>
            ))}
          </HistoryList>
        )}
      </Card>

      <SaveRow>
        <PrimaryBtn onClick={saveAll}>전체 저장</PrimaryBtn>
      </SaveRow>
    </Container>
  );
};

export default DbPricing;

const Container = styled.div``;

const GuideAccordion = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: 16px;
  overflow: hidden;
`;

const GuideHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const GuideToggle = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const GuideBody = styled.div`
  padding: 0 20px 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const GuideIntro = styled.p`
  margin: 14px 0;
  padding: 12px 14px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  line-height: 1.6;
`;

const GuideTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 14px;
  th, td {
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    font-size: 12px;
    text-align: left;
    vertical-align: top;
  }
  th {
    background: ${({ theme }) => theme.colors.borderLight};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const GuideList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    padding: 4px 0;
    font-size: 12px;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  > div { margin-bottom: 0; }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SubLabel = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
  margin-bottom: 6px;
`;

const MileagePreview = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SubDesc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 16px;
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: none;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const PrimaryBtn = styled.button`
  padding: 8px 20px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
`;

const SmallBtn = styled.button`
  padding: 6px 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: 500;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const CardTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  margin-bottom: -1px;
  background: none;
`;

const InlineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  &:focus-within { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Suffix = styled.span`
  padding: 9px 12px;
  background: ${({ theme }) => theme.colors.borderLight};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NumInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 9px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  text-align: right;
  background: #fff;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  ${InputGroup} & {
    border: none;
    border-radius: 0;
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 9px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const Select = styled.select`
  padding: 9px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: #fff;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const PriceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const IndTable = styled(PriceTable)`
  overflow-x: auto;
  display: block;
`;

const PTh = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const PThGroup = styled.th`
  text-align: center;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.borderLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const PThSub = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textLight};
  font-weight: 400;
`;

const PTd = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  vertical-align: middle;
`;

const MatrixWrap = styled.div`
  overflow-x: auto;
`;

const PriceMatrix = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding: 8px 10px;
    font-size: 12px;
    white-space: nowrap;
    text-align: center;
  }
  thead th {
    background: ${({ theme }) => theme.colors.borderLight};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const PMTh = styled.th`
  min-width: 100px;
`;

const PMRowHeader = styled.th`
  background: ${({ theme }) => theme.colors.borderLight};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: left !important;
  width: 110px;
`;

const PMPayType = styled.th`
  font-weight: 500;
  color: ${({ $type }) => ($type === '평당' ? '#2563EB' : '#9CA3AF')};
`;

const PMTd = styled.td`
  padding: 4px !important;
  input {
    text-align: right;
    width: 100%;
    min-width: 90px;
  }
`;

const PreviewBox = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const PreviewLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const PreviewLine = styled.div`
  display: flex;
  gap: 20px;
  font-size: 13px;
`;

const PreviewItem = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
`;

const BulkRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const HistoryTitle = styled.div`
  margin-top: 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const HistoryList = styled.div``;

const HistoryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const Empty = styled.div`
  padding: 20px 0;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const TabSaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -8px;
  margin-bottom: 16px;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
